import { css } from 'lit';

// Styles shared by the master-data pages: page header, table, form grid.
export const pageStyles = css`
  :host {
    display: block;
  }

  .page-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--wa-space-m);
    margin-bottom: var(--wa-space-m);
  }

  .page-header h2 {
    margin: 0;
    font-size: var(--wa-font-size-xl);
  }

  .spacer {
    flex: 1;
  }

  /* Tables scroll inside their wrapper instead of pushing the page wider. */
  .table-wrap {
    max-width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--wa-font-size-s);
  }

  th,
  td {
    text-align: left;
    padding: var(--wa-space-xs) var(--wa-space-s);
    border-bottom: 1px solid var(--wa-color-surface-border);
    vertical-align: top;
  }

  th {
    color: var(--wa-color-text-quiet);
    font-weight: var(--wa-font-weight-semibold);
    white-space: nowrap;
  }

  th.num,
  td.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  /* Headers wrap only where asked (long billing group names); the text sits
     on the baseline of the other headers. */
  th.wrap {
    white-space: normal;
    overflow-wrap: anywhere;
    vertical-align: bottom;
    max-width: 9rem;
  }

  td.actions {
    white-space: nowrap;
    text-align: right;
  }

  .muted {
    color: var(--wa-color-text-quiet);
  }

  .empty {
    padding: var(--wa-space-l);
    text-align: center;
    color: var(--wa-color-text-quiet);
  }

  form {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--wa-space-m);
  }

  form .full {
    grid-column: 1 / -1;
  }

  form h4 {
    grid-column: 1 / -1;
    margin: var(--wa-space-s) 0 calc(-1 * var(--wa-space-xs));
    font-size: var(--wa-font-size-xs);
    font-weight: var(--wa-font-weight-semibold);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--wa-color-text-quiet);
  }

  .mono {
    font-variant-numeric: tabular-nums;
  }

  .inline-field {
    grid-column: 1 / -1;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--wa-space-s);
  }

  .inline-field .label {
    font-weight: var(--wa-font-weight-semibold);
  }

  /* An input with an action button on the same row. */
  .with-button {
    display: flex;
    align-items: flex-start;
    gap: var(--wa-space-s);
  }

  .with-button wa-input {
    flex: 1;
  }

  .with-button wa-button {
    /* line the button up with the input, below its label */
    margin-top: calc(var(--wa-font-size-s) * var(--wa-line-height-normal) + var(--wa-space-2xs));
  }

  .check-lines {
    display: grid;
    gap: var(--wa-space-2xs);
  }

  .candidates {
    display: grid;
    gap: var(--wa-space-2xs);
    margin-top: var(--wa-space-xs);
    max-height: 14rem;
    overflow: auto;
  }

  .candidates .row {
    display: flex;
    align-items: center;
    gap: var(--wa-space-s);
  }

  .candidates .row .grow {
    flex: 1;
  }

  /* Timesheets: month toolbar, day grid, expanded rows. */
  .toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: end;
    gap: var(--wa-space-s);
    margin-bottom: var(--wa-space-m);
  }

  .toolbar wa-select,
  .toolbar wa-input {
    min-width: 14rem;
  }

  .day-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(3.2rem, 1fr));
    gap: 2px;
    margin: var(--wa-space-xs) 0;
  }

  .day {
    border: 1px solid var(--wa-color-surface-border);
    border-radius: var(--wa-border-radius-s);
    padding: 2px 4px;
    font-size: var(--wa-font-size-xs);
    line-height: 1.3;
    min-height: 2.4rem;
  }

  .day .d {
    color: var(--wa-color-text-quiet);
  }

  .day.weekend {
    background: var(--wa-color-neutral-fill-quiet);
  }

  .day.holiday {
    background: var(--wa-color-brand-fill-quiet);
  }

  .day.vacation,
  .day.other {
    background: var(--wa-color-warning-fill-quiet);
  }

  .day.sick {
    background: var(--wa-color-danger-fill-quiet);
  }

  tr.expanded td {
    background: var(--wa-color-neutral-fill-quiet);
  }

  .entries {
    width: 100%;
    font-size: var(--wa-font-size-xs);
  }

  .warn {
    color: var(--wa-color-danger-on-quiet);
    font-weight: var(--wa-font-weight-semibold);
  }

  .editable-table td {
    vertical-align: middle;
  }

  .editable-table wa-input,
  .editable-table wa-select {
    min-width: 8rem;
  }

  .form-error {
    grid-column: 1 / -1;
  }

  .footer {
    display: flex;
    gap: var(--wa-space-s);
    justify-content: flex-end;
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: var(--wa-space-2xs);
  }

  /* Link chips wrap their text so a long label does not squeeze the other columns. */
  .chips wa-tag {
    max-width: 100%;
    height: auto;
    min-height: 0;
    white-space: normal;
    line-height: 1.3;
    padding-top: 2px;
    padding-bottom: 2px;
  }

  .chips wa-tag::part(content) {
    white-space: normal;
    overflow-wrap: anywhere;
    overflow: visible;
    text-overflow: clip;
  }

  td.date {
    white-space: nowrap;
  }

  /* File names and other long tokens break rather than widen the table. */
  td.file,
  td.wrap {
    overflow-wrap: anywhere;
  }

  .filters {
    display: flex;
    flex-wrap: wrap;
    align-items: end;
    gap: var(--wa-space-s);
    margin-bottom: var(--wa-space-m);
  }

  .filters wa-input,
  .filters wa-select {
    min-width: 14rem;
  }

  .sub {
    display: block;
    color: var(--wa-color-text-quiet);
    font-size: var(--wa-font-size-xs);
  }

  input[type='file'] {
    font: inherit;
    max-width: 100%;
  }

  /* ---- Responsive rules. Breakpoints: 900px (tablet portrait and below),
     640px (phones). Plain tables scroll sideways below 900px; tables marked
     .cards turn into stacked cards below 900px, each cell labelled from its
     data-label attribute. ---- */
  @media (max-width: 900px) {
    .filters wa-input,
    .filters wa-select,
    .toolbar wa-input,
    .toolbar wa-select {
      min-width: 0;
      flex: 1 1 12rem;
    }

    /* Header notes (e.g. "Last import …") may wrap instead of overflowing. */
    .page-header .muted {
      min-width: 0;
      overflow-wrap: anywhere;
    }

    table.cards {
      display: block;
    }

    table.cards thead {
      display: none;
    }

    table.cards tbody,
    table.cards tr {
      display: block;
    }

    table.cards tr {
      border: 1px solid var(--wa-color-surface-border);
      border-radius: var(--wa-border-radius-m);
      padding: var(--wa-space-xs) var(--wa-space-s);
      margin-bottom: var(--wa-space-s);
    }

    table.cards td,
    table.cards th[data-label] {
      display: flex;
      flex-wrap: wrap;
      gap: var(--wa-space-2xs) var(--wa-space-s);
      align-items: baseline;
      border: 0;
      padding: var(--wa-space-2xs) 0;
      text-align: left;
      min-width: 0;
      overflow-wrap: anywhere;
    }

    table.cards td > *,
    table.cards th[data-label] > * {
      min-width: 0;
    }

    table.cards th[data-label] {
      font-weight: var(--wa-font-weight-semibold);
      color: var(--wa-color-text-normal);
      white-space: normal;
    }

    table.cards td[data-label]::before,
    table.cards th[data-label]::before {
      content: attr(data-label);
      flex: 0 0 6.5rem;
      color: var(--wa-color-text-quiet);
      font-size: var(--wa-font-size-xs);
      line-height: var(--wa-line-height-normal);
    }

    table.cards td.num {
      justify-content: flex-start;
      text-align: left;
    }

    table.cards td.actions {
      justify-content: flex-start;
      flex-wrap: wrap;
      white-space: normal;
      padding-top: var(--wa-space-xs);
    }

    table.cards td.empty-cell {
      display: none;
    }

    /* A secondary line sits under the value, indented past the label. */
    table.cards td .sub {
      flex: 1 1 100%;
      margin-left: calc(6.5rem + var(--wa-space-s));
    }

    /* Tags and chips may wrap instead of forcing a wide row. */
    table.cards wa-tag {
      max-width: 100%;
    }

    table.cards wa-tag::part(base) {
      height: auto;
      white-space: normal;
      line-height: 1.3;
      padding-top: 2px;
      padding-bottom: 2px;
    }
  }

  @media (max-width: 640px) {
    form {
      grid-template-columns: 1fr;
    }

    .filters wa-input,
    .filters wa-select,
    .toolbar wa-input,
    .toolbar wa-select {
      flex: 1 1 100%;
    }

    .filters wa-input[type='date'] {
      flex: 1 1 40%;
    }

    .page-header .muted {
      flex: 1 1 100%;
    }

    .footer {
      flex-wrap: wrap;
    }

    .footer wa-button {
      flex: 1 1 auto;
    }

    /* Secondary columns stay out of the way on phones. */
    table.cards td.m-hide,
    table.cards th.m-hide {
      display: none;
    }
  }
`;
